import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { HardHat, Wrench, Shield, Trophy, CheckCircle, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const Construction = () => {
  const constructionServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://greenfrogutilities.co.uk/services/construction#service",
    "name": "Utility Construction",
    "serviceType": "Utility infrastructure construction",
    "description": "Construction of complex utility infrastructure for commercial and industrial sites, including excavation, groundworks, mains laying and reinstatement.",
    "provider": { "@id": "https://greenfrogutilities.co.uk/#organization" },
    "areaServed": { "@type": "Country", "name": "United Kingdom" }
  };

  const constructionBreadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://greenfrogutilities.co.uk/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://greenfrogutilities.co.uk/services/connections" },
      { "@type": "ListItem", "position": 3, "name": "Utility Construction", "item": "https://greenfrogutilities.co.uk/services/construction" }
    ]
  };

  const features = [
    {
      icon: Wrench,
      title: 'Expert Installation',
      description: 'Skilled engineers and technicians delivering high-quality construction work'
    },
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Rigorous safety standards and compliance with all health and safety regulations'
    },
    {
      icon: Trophy,
      title: 'Quality Assured',
      description: 'All work completed to the highest standards with comprehensive quality checks'
    },
    {
      icon: HardHat,
      title: 'Complex Projects',
      description: 'Experience handling large-scale and technically challenging utilities projects'
    }
  ];

  const services = [
    'Gas pipeline installation and modifications',
    'Electrical infrastructure construction',
    'Substation and switchgear installation',
    'Underground cable and duct installation',
    'Service entry equipment installation',
    'Excavation and reinstatement works',
    'Testing, commissioning and certification',
    'Project management and coordination'
  ];

  return (
    <div>
      <SEO 
        title="Industrial Gas Pipeline Installation UK | Gas Utility Contractors | Green Frog Utilities"
        description="Expert gas utility contractors UK. Industrial gas pipeline installation, high capacity gas meter installation, gas metering for factories, manufacturing gas metering solutions, bulk gas metering systems. GIRS accredited."
        keywords="industrial gas pipeline installation UK, gas utility contractors UK, industrial gas metering solutions, gas metering for factories UK, manufacturing gas metering solutions, bulk gas metering systems, high capacity gas meter installation, gas meter installation for industrial units"
        canonical="/services/construction"
        jsonLd={[constructionServiceSchema, constructionBreadcrumbSchema]}
      />
      {/* Hero Section */}
      <section className="relative h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/hq7bk3hn_Screenshot%202026-03-19%20at%2010.59.56.png)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/40 to-emerald-700/20" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl text-white">
            <div className="flex items-center gap-3 mb-4">
              <HardHat className="h-12 w-12" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">
                Industrial Gas Pipeline Installation UK
              </h1>
            </div>
            <p className="text-xl text-emerald-100">
              Gas utility contractors UK with the knowledge and experience to handle all aspects of complex utilities construction. High pressure gas connections and medium pressure gas installation services.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Utilities Construction Excellence
              </h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  Green Frog Utilities brings extensive experience in utilities construction, delivering projects of all sizes and complexity. Our skilled teams are equipped to handle everything from straightforward installations to large-scale, technically challenging projects.
                </p>
                <p>
                  We pride ourselves on our attention to detail, commitment to safety, and ability to work efficiently while maintaining the highest quality standards. Our construction services are fully integrated with our design and connection capabilities, ensuring seamless project delivery.
                </p>
                <p>
                  From excavation through to final testing and commissioning, we manage all aspects of the construction process, keeping your project on schedule and within budget.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/contact">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    Request a Construction Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-8 rounded-lg flex items-center justify-center">
                <div className="grid grid-cols-3 gap-4">
                  <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <HardHat className="h-8 w-8 text-white" />
                  </div>
                  <div className="w-16 h-16 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <Wrench className="h-8 w-8 text-white" />
                  </div>
                  <div className="w-16 h-16 bg-emerald-400 rounded-lg flex items-center justify-center">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <div className="w-16 h-16 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <Trophy className="h-8 w-8 text-white" />
                  </div>
                  <div className="w-16 h-16 bg-emerald-400 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                  <div className="w-16 h-16 bg-emerald-300 rounded-lg flex items-center justify-center">
                    <ArrowRight className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Construction Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Delivering complex utilities projects with expertise and precision
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-none hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6 inline-flex p-4 bg-emerald-50 rounded-full">
                      <Icon className="h-10 w-10 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Construction Services Include
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Comprehensive construction capabilities for all types of utilities infrastructure projects.
              </p>
              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/hq7bk3hn_Screenshot%202026-03-19%20at%2010.59.56.png"
                alt="Utilities Construction"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Quality */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Safety & Quality Standards
            </h2>
            <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
              We never compromise on safety or quality
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="mb-4 inline-flex p-4 bg-white/10 rounded-full">
                <Shield className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-2">Safety Compliance</h3>
              <p className="text-emerald-100">
                Full compliance with CDM regulations and health & safety requirements
              </p>
            </div>
            <div className="text-center">
              <div className="mb-4 inline-flex p-4 bg-white/10 rounded-full">
                <Trophy className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-2">Quality Assurance</h3>
              <p className="text-emerald-100">
                Rigorous quality control processes and comprehensive testing procedures
              </p>
            </div>
            <div className="text-center">
              <div className="mb-4 inline-flex p-4 bg-white/10 rounded-full">
                <HardHat className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-bold mb-2">Trained Teams</h3>
              <p className="text-emerald-100">
                Fully qualified and certified engineers and construction personnel
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Construction Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A methodical approach ensuring safe, efficient delivery
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Planning', desc: 'Detailed method statements and risk assessments' },
              { step: '02', title: 'Mobilization', desc: 'Site setup and resource deployment' },
              { step: '03', title: 'Construction', desc: 'Installation works with ongoing quality checks' },
              { step: '04', title: 'Completion', desc: 'Testing, commissioning, and project handover' }
            ].map((phase, index) => (
              <Card key={index} className="border-emerald-200">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold text-emerald-600 mb-4">{phase.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.title}</h3>
                  <p className="text-gray-600">{phase.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to start your construction project?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact us today to discuss your utilities construction requirements
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Get a Quote
                </Button>
              </Link>
              <a href="https://greenfrogassetmanagement.co.uk" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                  View Metering Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Construction;
